import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getUserData } from 'utils';
import { createAPIEndPoint } from 'config/api/api';
import toast from 'react-hot-toast';

const PatientModal = ({ isOpen, onClose, type, patientId, fetchAgain }) => {
  const userData = getUserData();
  const clinic_id = userData?.clinic_id ?? 1;

  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required('First Name is required'),
    last_name: Yup.string().required('Last Name is required'),
    // email: Yup.string().email('Invalid email address').required('Email is required'),
    phone: Yup.string()
      .matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Phone number must be in format (XXX) XXX-XXXX')
      .required('Phone number is required'),
    // address: Yup.string().required('Address is required'),
    // state: Yup.string().required('State is required'),
    // postal_code: Yup.string().required('Postal Code is required'),
    // insurance_name: Yup.string().required('Insurance Name is required'),
    // insurance_no: Yup.string().required('Insurance Number is required'),
    // date_of_birth: Yup.date()
    //   .required('Date of Birth is required')
    //   .max(new Date(), 'Date of Birth cannot be in the future'),
  });

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, ''); // Remove non-numeric characters
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

    if (!match) return value;

    const formatted = !match[2]
      ? match[1]
      : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`;

    return formatted.length > 14 ? formatted.slice(0, 14) : formatted; // Limit to 14 characters
  };

  const [patientData, setPatientData] = useState([]);

  const fetchPatient = async () => {
    try {
      const response = await createAPIEndPoint('patient/').fetchById(patientId);
      console.log('🚀 ~ fetchPatient ~ response:', response.data);
      setPatientData(response.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error fetching patient');
    }
  };

  // useEffect(() => {
  //   if (patientId) {
  //     fetchPatient();
  //   }
  // }, [patientId, isOpen]);

  useEffect(() => {
    if (patientId && type === 'edit') {
      fetchPatient();
    }
  }, [patientId, type]);



  const handleCreate = async (values, { resetForm }) => {
    try {
      setLoading(true);
      if (patientId) {
        await createAPIEndPoint('patient/').update(patientId, {
          ...values,
          postal_code: values.postal_code.trim(),
          clinic_id,
        });
      } else {
        await createAPIEndPoint('patient').create({
          ...values,
          postal_code: values.postal_code.trim(),
          clinic_id,
        });
      }
      toast.success('Patient added successfully!');
      setLoading(false);
      resetForm();
      fetchAgain();
      onClose();
      setPatientData(null);
    } catch (error) {
      setLoading(false);
      toast.error(error.response?.data?.error || 'An error occurred while creating the patient.');
    }
  };


  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: patientData?.name?.split(' ')[0] || '',
      last_name: patientData?.name?.split(' ').slice(1).join(' ') || '',
      email: patientData?.email || '',
      phone: patientData?.phone || '',
      address: patientData?.address || '',
      state: patientData?.state || '',
      postal_code: patientData?.postal_code || '',
      insurance_name: patientData?.insurance_name || '',
      insurance_no: patientData?.insurance_no || '',
      date_of_birth: patientData?.date_of_birth || '',
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      const payload = {
        ...values,
        name: `${values.first_name} ${values.last_name}`, // Combine first and last name
        clinic_id,
      };
      delete payload.first_name;
      delete payload.last_name;

      handleCreate(payload, { resetForm });
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setPatientData([]);
      }}
      isCentered
    >
      <ModalOverlay />
      <ModalContent borderRadius="16px" pb="16px">
        <ModalHeader color="brand.500">
          {type === 'edit' ? 'Edit Patient' : 'Add Patient'}
        </ModalHeader>
        <ModalCloseButton />
          <form onSubmit={formik.handleSubmit}>
        <ModalBody maxHeight="70dvh" overflowY="auto" pb={2}>
            {Object.keys(formik.initialValues).map((field) => (
              <FormControl
                key={field}
                isInvalid={formik.touched[field] && formik.errors[field]}
                mb={2}
              >
                <FormLabel mb={0}>
                  {field === 'insurance_no' ? 'Insurance / Policy' :
                    field
                      .replace(/_/g, ' ')
                      .toLowerCase()
                      .replace(/\b\w/g, (char) => char.toUpperCase())}
                </FormLabel>

                <Input
                  name={field}
                  type={field === 'date_of_birth' ? 'date' : 'text'} // Use 'date' for DOB, 'tel' for phone, otherwise 'text'
                  placeholder={`Enter ${field === 'insurance_no' ? 'Insurance / Policy' :
                    field
                      .replace(/_/g, ' ')
                      .toLowerCase()
                      .replace(/\b\w/g, (char) => char.toUpperCase())}`}
                  variant="auth"
                  fontSize="sm"
                  fontWeight="500"
                  maxLength={field === 'phone' ? 14 : undefined} // Limit length for phone input
                  value={formik.values[field]}
                  onChange={(e) =>
                    field === 'phone'
                      ? formik.handleChange({
                        target: {
                          name: 'phone',
                          value: formatPhoneNumber(e.target.value),
                        },
                      })
                      : formik.handleChange(e)
                  }
                  onBlur={formik.handleBlur}
                />
                {formik.touched[field] && formik.errors[field] && (
                  <Text color="red.500" fontSize="sm">
                    {formik.errors[field]}
                  </Text>
                )}
              </FormControl>
            ))}

        </ModalBody>
        <ModalFooter pt={0}>
          <Button
            type="submit"
            colorScheme="brand"
            w="100%"
            mt={4}
            isLoading={loading}
            >
            {type === 'edit' ? 'Update Patient' : 'Add Patient'}
          </Button>
        </ModalFooter>
            </form>
      </ModalContent>
    </Modal>
  );
};

export default PatientModal;
